import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Chip,
  Button,
  Stack,
  Alert,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import { useGetAdminProfileQuery } from "../../../redux/api/authApi";
import { authStorageGet } from "../../../utils/authStorage";
import { useAppSelector } from "../../../redux/hook";
import {
  formatRoleLabel,
  getUserAvatarInitials,
  getUserDisplayName,
} from "../../../utils/userDisplay";
import InvestorConnectWalletDialog, {
  useDisconnectLinkedWallet,
} from "../../investor/InvestorConnectWalletDialog";
import { ADMIN_WALLET_STORAGE_KEYS } from "../adminWalletStorageKeys";
import AdminSettingsSkeleton from "./AdminSettingsSkeleton";

const WALLET_CHAIN_ID = Number(process.env.REACT_APP_WALLET_CHAIN_ID) || 1;

function blockExplorerAddressUrl(address) {
  const a = String(address || "");
  if (!a) return "#";
  if (WALLET_CHAIN_ID === 137) return `https://polygonscan.com/address/${a}`;
  if (WALLET_CHAIN_ID === 56) return `https://bscscan.com/address/${a}`;
  return `https://etherscan.io/address/${a}`;
}

function networkLabel(chainId) {
  if (chainId === 137) return "Polygon Network";
  if (chainId === 56) return "BNB Chain";
  if (chainId === 1) return "Ethereum Mainnet";
  return `Chain ID: ${chainId}`;
}

function formatProfileDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function formatIdTypeLabel(raw) {
  if (raw == null || String(raw).trim() === "") return "";
  return String(raw)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatNationalityLabel(raw) {
  if (raw == null || String(raw).trim() === "") return "—";
  return String(raw)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export default function AdminSettings() {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth?.user);

  const {
    data: adminProfileRes,
    isLoading: profileLoading,
    isError: profileError,
    error: profileErr,
    refetch: refetchAdminProfile,
  } = useGetAdminProfileQuery();

  const profile = useMemo(() => {
    const root = adminProfileRes;
    if (!root || typeof root !== "object") return null;
    return root.data ?? null;
  }, [adminProfileRes]);

  const mergedUser = useMemo(() => {
    if (!profile) return user;
    return {
      ...user,
      firstName: profile.firstName ?? user?.firstName,
      lastName: profile.lastName ?? user?.lastName,
      email: profile.email ?? user?.email,
    };
  }, [user, profile]);

  const displayName = getUserDisplayName(mergedUser);
  const initials = getUserAvatarInitials(mergedUser);
  const roleLabel = formatRoleLabel(profile?.role ?? user?.role);

  const [walletAddress, setWalletAddress] = useState(
    () => authStorageGet(ADMIN_WALLET_STORAGE_KEYS.address) || ""
  );
  const [walletProvider, setWalletProvider] = useState(
    () => authStorageGet(ADMIN_WALLET_STORAGE_KEYS.provider) || ""
  );
  const [connectOpen, setConnectOpen] = useState(false);
  const disconnectLinked = useDisconnectLinkedWallet(ADMIN_WALLET_STORAGE_KEYS);

  const effectiveWalletAddress = String(
    walletAddress || profile?.walletAddress || ""
  ).trim();

  const handleWalletLinked = useCallback(({ address, provider }) => {
    setWalletAddress(address || "");
    setWalletProvider(provider || "");
  }, []);

  const handleDisconnectWallet = useCallback(async () => {
    await disconnectLinked(effectiveWalletAddress);
    setWalletAddress("");
    setWalletProvider("");
    refetchAdminProfile();
  }, [effectiveWalletAddress, disconnectLinked, refetchAdminProfile]);

  const apiWalletVerified = !!profile?.walletVerified;
  const apiWalletVerifiedAt = profile?.walletVerifiedAt ?? null;

  const accountTypeLine = (() => {
    const typeRaw =
      profile?.investorType ??
      profile?.ownerType ??
      profile?.adminType ??
      null;
    const typeFormatted = typeRaw ? formatIdTypeLabel(typeRaw) : "";
    const base = roleLabel ? `${roleLabel} account` : "Account";
    return typeFormatted ? `${base} · ${typeFormatted}` : base;
  })();

  if (profileLoading && !profile) {
    return <AdminSettingsSkeleton />;
  }

  if (profileError || !profile) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          px: 2,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Alert
          severity="error"
          onClose={() => refetchAdminProfile()}
          sx={{ maxWidth: 480 }}
        >
          {profileErr?.data?.message ||
            profileErr?.error ||
            "Unable to load profile. Please try again later."}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        display: "flex",
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <Button
          onClick={() => navigate("/admin")}
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: "none",
            color: "text.primary",
            mb: 2,
            px: 0,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          Back to Dashboard
        </Button>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: 1,
              bgcolor: "background.headerIcon",
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SettingsOutlinedIcon sx={{ color: "text.slate" }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: "text.primary" }}
            >
              Account &amp; Settings
            </Typography>
            <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
              Manage your profile and security
            </Typography>
          </Box>
        </Stack>

        {/* PROFILE INFORMATION */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: 3,
            mb: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            Profile Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your verified identity and contact details
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2.5}>
                <Avatar sx={{ width: 64, height: 64, fontSize: 22 }}>
                  {initials}
                </Avatar>
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      {displayName}
                    </Typography>
                    {/* {String(profile?.kycStatus || "")
                      .toLowerCase()
                      .includes("verified") ||
                    profile?.kycStatus === "Approved" ? (
                      <Chip
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        label="KYC verified"
                        sx={{
                          backgroundColor: (theme) =>
                            theme.palette.accent.success.bg,
                          color: (theme) => theme.palette.accent.success.text,
                          "& .MuiChip-icon": {
                            color: (theme) =>
                              theme.palette.accent.success.text,
                          },
                        }}
                      />
                    ) : profile?.kycStatus ? (
                      <Chip
                        size="small"
                        label={profile.kycStatus}
                        color="warning"
                        variant="outlined"
                      />
                    ) : null} */}
                    {profile?.status ? (
                      <Chip
                        size="small"
                        label={profile.status}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : null}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {accountTypeLine}
                  </Typography>
                  {profile?.nationality ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Nationality:{" "}
                      {formatNationalityLabel(profile.nationality)}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Email Address
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mt={0.5}
                  >
                    <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2">
                      {mergedUser?.email || "—"}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Phone Number
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mt={0.5}
                  >
                    <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2">
                      {profile?.phoneNumber != null &&
                      String(profile.phoneNumber).trim() !== ""
                        ? String(profile.phoneNumber).trim().startsWith("+")
                          ? String(profile.phoneNumber).trim()
                          : `+${String(profile.phoneNumber).trim()}`
                        : "—"}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>

            {/* <Grid size={{ xs: 12, md: 6 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      height: "100%",
                      backgroundColor: (theme) =>
                        theme.palette.accent.success.softer,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      Identity Verification
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Nafath Authentication
                    </Typography>
                    <Chip
                      size="small"
                      label={profile?.nafathStatus || "—"}
                      sx={{
                        mt: 1.5,
                        backgroundColor: (theme) =>
                          String(profile?.nafathStatus || "")
                            .toLowerCase()
                            .includes("approved") || profile?.verifiedByNafath
                            ? theme.palette.accent.success.bg
                            : theme.palette.accent.warning.pendingBg,
                        color: (theme) =>
                          String(profile?.nafathStatus || "")
                            .toLowerCase()
                            .includes("approved") || profile?.verifiedByNafath
                            ? theme.palette.accent.success.text
                            : theme.palette.accent.warning.pendingText,
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {profile?.nafathVerifiedAt
                        ? `Updated ${formatProfileDate(profile.nafathVerifiedAt)}`
                        : "—"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid> */}
          </Grid>

          {/* <Paper
            elevation={0}
            sx={{
              mt: 3,
              borderRadius: 2,
              p: 2,
              background:
                "linear-gradient(90deg, #e8f6ff 0%, #f0f9ff 50%, #e8fdfa 100%)",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              KYC status:
            </Typography>{" "}
            <Typography variant="caption" component="span">
              {profile?.kycStatus || "—"}
              {profile?.kycVerifiedAt
                ? ` · Verified on ${formatProfileDate(profile.kycVerifiedAt)}`
                : ""}
              {profile && !profile.kycVerifiedAt && profile.kycStatus
                ? ". Complete any pending checks in your verification cards above."
                : ""}
            </Typography>
          </Paper> */}
        </Paper>

        {/* MY WALLET */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: 3,
            mb: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                My Wallet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your blockchain wallet and earnings overview
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AccountBalanceWalletOutlined />}
              onClick={() => setConnectOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: "#0d9488",
                "&:hover": { bgcolor: "#0f766e" },
              }}
            >
              {effectiveWalletAddress ? "Change Wallet" : "Connect Wallet"}
            </Button>
          </Stack>

          {!effectiveWalletAddress ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                p: 4,
                textAlign: "center",
                borderColor: "#e5e7eb",
                mb: 0,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "#e0f2fe",
                  display: "grid",
                  placeItems: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <AccountBalanceWalletOutlined
                  sx={{ fontSize: 36, color: "#0284c7" }}
                />
              </Box>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                No Wallet Connected
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 420, mx: "auto" }}
              >
                Connect your crypto wallet to view your balance, earnings, and
                transaction history.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AccountBalanceWalletOutlined />}
                onClick={() => setConnectOpen(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 4,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: "#0284c7",
                  "&:hover": { bgcolor: "#0369a1" },
                }}
              >
                Connect Wallet
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2 }}
              >
                Supported wallets: MetaMask, WalletConnect, and Coinbase Wallet
              </Typography>
            </Paper>
          ) : (
            <Paper
              sx={{
                borderRadius: 3,
                p: 3,
                background:
                  "linear-gradient(90deg,#06124c 0%,#071b5f 40%,#060e3d 100%)",
                color: "#ffffff",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                gap={1}
              >
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  Wallet Address
                </Typography>
                <Stack direction="row" spacing={0.75}>
                  {apiWalletVerified && (
                    <Chip
                      label="Verified"
                      size="small"
                      sx={{
                        backgroundColor: "#22c55e",
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    />
                  )}
                  <Chip
                    label={
                      walletProvider
                        ? `${walletProvider} · Connected`
                        : "Connected"
                    }
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.18)",
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              </Stack>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  mt: 1,
                  fontWeight: 600,
                }}
              >
                {effectiveWalletAddress}
              </Typography>
              <Typography
                variant="caption"
                sx={{ mt: 1.5, display: "block", opacity: 0.85 }}
              >
                {networkLabel(WALLET_CHAIN_ID)} (Chain ID: {WALLET_CHAIN_ID})
              </Typography>
              {apiWalletVerifiedAt && (
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, display: "block", opacity: 0.7 }}
                >
                  Verified on {formatProfileDate(apiWalletVerifiedAt)}
                </Typography>
              )}
              <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 2 }}>
                <Button
                  component="a"
                  href={blockExplorerAddressUrl(effectiveWalletAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  endIcon={<OpenInNewIcon />}
                  sx={{
                    color: "#ffffff",
                    borderColor: "rgba(255,255,255,0.45)",
                    textTransform: "none",
                    "&:hover": { borderColor: "#ffffff" },
                  }}
                >
                  View on Explorer
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleDisconnectWallet}
                  sx={{
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    background: "#e91212",
                    "&:hover": { background: "#c0392b" },
                  }}
                >
                  Disconnect
                </Button>
              </Stack>
            </Paper>
          )}

          <InvestorConnectWalletDialog
            open={connectOpen}
            onClose={() => setConnectOpen(false)}
            onLinked={handleWalletLinked}
            storageKeys={ADMIN_WALLET_STORAGE_KEYS}
          />
        </Paper>
      </Box>
    </Box>
  );
}
